import { useTranslation } from 'react-i18next';
import { HardHat } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

/** N29.1 — la question « Build a-t-il confirmé ? » est-elle posée ? Sur un ticket où Build a RÉPONDU
 *  (marqueur durable : synchro, commentaire d'une personne de Build — N29.5 — ou admin), et toujours depuis la console DMP (admin, sans ticket — choix ② du 24-09).
 *  Jamais ailleurs : la file MDU crée sans ticket, donc sans réponse de Build possible. Le serveur applique la même
 *  règle (pst-bis.service) : ici on ne pose pas une question inutile, là-bas on refuse un « Oui » illégitime.
 *  Typage structurel, et non AddressMgmtContext : ce module est importé par les deux formulaires, sans cycle. */
export function pstBisOffered(
  context?: { viaConsole?: boolean; ticketId?: string | null; buildRepliedAt?: string | null },
): boolean {
  return !!context?.viaConsole || !!(context?.ticketId && context?.buildRepliedAt);
}

// N29.1 (plan validé le 24-09), reformulée par N29.4 (décisions du user, 30-09) — au clic sur « Créer » (adresse)
// ou sur « Ajouter » (boîtes), APRÈS les confirmations existantes, jamais par-dessus. La question n'est plus
// « Build doit-il avoir cette adresse ? » mais « Build a-t-il CONFIRMÉ sa création ? ». Trois sorties :
//  · « Oui, Build a confirmé » : l'adresse est créée MARQUÉE dans le DMP (N29.8, accord d'Anouk du 12-10 : build
//    list et deployment list à la date du jour, belmap_version = 1 — le serveur le fait dans l'INSERT même, quelle
//    que soit la case « Dans la liste de déploiement »), et partira au fichier PST bis à sa release ;
//  · « Non » : sur un ticket, RIEN n'est créé et la page prépare le refus (verdict « Rejeté » + motif, ou, sans JVID,
//    le message à l'opérateur) sans rien envoyer ; depuis la console, sans ticket à refuser, création sans PST bis ;
//  · « Revenir au formulaire » — et donc Échap, et le clic hors de la fenêtre — n'écrit RIEN.
// Aucune des deux réponses n'est mise en avant (choix du 30-09) : le focus d'ouverture va à « Revenir ».
// Rendue DANS le formulaire, donc dans le Sheet, comme les autres fenêtres de ces formulaires : ConfirmDialog ne se
// portalise pas, et ses clics restent « dedans » pour Radix.
export function PstBisDialog({
  open, kind, viaConsole, refusal = 'verdict', busy, onYes, onNo, onCancel,
}: {
  open: boolean;
  kind: 'address' | 'boxes';
  /** Console DMP (admin, sans ticket) : la question y est toujours posée, et le texte ne parle pas de ticket. */
  viaConsole: boolean;
  /** Ce que « Non » préparera sur un ticket, pour l'annoncer : le verdict (ticket lié) ou le message (sans JVID). */
  refusal?: 'verdict' | 'message';
  busy?: boolean;
  onYes: () => void;
  onNo: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('tickets');
  const body = viaConsole
    ? t(kind === 'boxes' ? 'addressMgmt.pstBis.bodyConsoleBoxes' : 'addressMgmt.pstBis.bodyConsole')
    : t(kind === 'boxes' ? 'addressMgmt.pstBis.bodyTicketBoxes' : 'addressMgmt.pstBis.bodyTicket');
  const consequence = viaConsole
    ? t('addressMgmt.pstBis.consequenceConsole')
    : t(refusal === 'message' ? 'addressMgmt.pstBis.consequenceMessage' : 'addressMgmt.pstBis.consequenceVerdict');
  return (
    <ConfirmDialog
      open={open}
      tone="sky"
      icon={<HardHat className="h-5 w-5" />}
      title={t(kind === 'boxes' ? 'addressMgmt.pstBis.titleBoxes' : 'addressMgmt.pstBis.titleAddress')}
      message={(
        <div className="space-y-2 text-left">
          <p>{body}</p>
          <p className="text-xs text-slate-500">{consequence}</p>
        </div>
      )}
      confirmLabel={t(viaConsole ? 'addressMgmt.pstBis.noConsole' : 'addressMgmt.pstBis.noTicket')}
      confirmEmphasis="none"
      cancelLabel={t('addressMgmt.pstBis.cancel')}
      secondaryLabel={t('addressMgmt.pstBis.yes')}
      onSecondary={onYes}
      busy={busy}
      onCancel={onCancel}
      onConfirm={onNo}
    />
  );
}
